import React, { useContext, useMemo } from 'react';

import { RicosContext } from './../../ricos-context';
import type { HTMLData } from './../../ricos-schema/next-schema';
import type { LegacyEditorPluginConfig } from './../../ricos-types';
import { containsUrl } from './../../_shared/link/contains-url';
import { normalizeUrl } from './../../_shared/link/normalize-url';

import { ROOT } from '../data-hooks';
import { INIT_HEIGHT, INIT_WIDTH, SRC_TYPE_HTML, SRC_TYPE_URL } from '../defaults';
import { htmlFixer } from '../htmlFixer';
import { IframeHtml } from '../IframeHtml';
import IframeUrl from '../IframeUrl';
import htmlStyles from '../statics/styles/HtmlComponent-scss';

const getPageURL = (siteDomain: $TSFixMe) => {
  if (!siteDomain) {
    return;
  }

  const regex = /http.+com/gm;
  const res = regex.exec(siteDomain);
  if (res) {
    return res[0];
  }
};

export const HtmlViewer: React.FC<{
  settings: LegacyEditorPluginConfig;
  htmlData: HTMLData;
  iframeSandboxDomain?: string;
  isViewer?: boolean;
  onIframeHeightChange?: (height: number) => void;
}> = ({ settings, htmlData, iframeSandboxDomain, onIframeHeightChange, isViewer = false }) => {
  const { isMobile } = useContext(RicosContext);
  const [iframeHeight, setIframeHeight] = React.useState<number | undefined>();

  const setHeight = (height: number) => {
    if (height > 0 && height !== iframeHeight) {
      setIframeHeight(height);
    }
  };

  const getIframeHeight = useMemo(() => {
    const { containerData, autoHeight } = htmlData;

    if (autoHeight !== false && iframeHeight) {
      onIframeHeightChange?.(iframeHeight);

      return iframeHeight;
    }

    const customHeight = containerData?.height?.custom;

    const parsedCustomHeight = parseInt(customHeight || '');
    if (
      !customHeight ||
      (typeof customHeight === 'string' && customHeight.endsWith('%')) ||
      isNaN(parsedCustomHeight)
    ) {
      return iframeHeight || settings.height || INIT_HEIGHT;
    }

    return parsedCustomHeight;
  }, [htmlData, settings.height, iframeHeight]);

  const style = {
    width: isMobile
      ? 'auto'
      : htmlData.containerData?.width?.custom || settings.width || INIT_WIDTH + 'px',
    height: getIframeHeight + 'px',
    maxHeight: getIframeHeight + 'px',
  };

  const html = useMemo(() => {
    const pageURL = getPageURL(settings.siteDomain);
    return htmlFixer(htmlData.html, pageURL);
  }, [htmlData.html]);

  return (
    <div className={htmlStyles.htmlComponent} style={style} data-hook={ROOT}>
      {html ? (
        <IframeHtml
          iframeSandboxDomain={iframeSandboxDomain || 'https://www.filesusr.com'}
          key={SRC_TYPE_HTML}
          html={html}
          onHeightChange={setHeight}
          isViewer={isViewer}
        />
      ) : htmlData.url && containsUrl(htmlData.url) ? (
        <IframeUrl
          key={SRC_TYPE_URL}
          // @ts-ignore
          tabIndex={0}
          src={normalizeUrl(htmlData.url)}
          isViewer={isViewer}
        />
      ) : (
        <div className={htmlStyles.htmlComponent_placeholder} />
      )}
    </div>
  );
};
